import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaPlus, FaTimes, FaInfoCircle } from 'react-icons/fa';
import SearchDropdown from '../../../../../../components/common/SearchDropdown/SearchDropdown';
import { getAllUnidadesMedida, getAllUnitConversions } from '../../../../../../crud/configuration_api/unidades_medida';
import { getAllIngredientsByLocalId } from '../../../../../../crud/menu_api/ingredients';
import './IngredientCompositionManager.scss';

export interface Option { 
  id: number; 
  name: string; 
  unidad_medida_id?: number 
}

export interface IngredientSelection {
  id: number | string;
  name: string;
  units: number | null;
  unidad_medida_id: number;
}

export interface UnitConversion {
  from_unidad_medida_id: number;
  to_unidad_medida_id: number;
  factor: number;
  from_name: string;
  from_acronym: string;
  to_name: string;
  to_acronym: string;
}

interface IngredientCompositionManagerProps {
  ingredientSelections: IngredientSelection[];
  setIngredientSelections: React.Dispatch<React.SetStateAction<IngredientSelection[]>>;
  currentIngredientId?: number; // To exclude from selection
}

const IngredientCompositionManager: React.FC<IngredientCompositionManagerProps> = ({
  ingredientSelections,
  setIngredientSelections,
  currentIngredientId,
}) => {
  const [ingredientOptions, setIngredientOptions] = useState<Option[]>([]);
  const [unidadMedidaOptions, setUnidadMedidaOptions] = useState<Option[]>([]);
  const [unitConversions, setUnitConversions] = useState<UnitConversion[]>([]);
  const [loading, setLoading] = useState(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const localId = user?.local_id;

  // Function to get available units for a specific ingredient
  const getAvailableUnitsForIngredient = (ingredientId: number | string): Option[] => {
    if (!ingredientId) return unidadMedidaOptions;
    
    // Find the selected ingredient and its base unit
    const selectedIngredient = ingredientOptions.find(ing => ing.id === ingredientId);
    if (!selectedIngredient || !selectedIngredient.unidad_medida_id) {
      return unidadMedidaOptions;
    }
    
    const baseUnitId = selectedIngredient.unidad_medida_id;
    
    // Always include the base unit
    const availableUnits = unidadMedidaOptions.filter(unit => unit.id === baseUnitId);
    
    // Find convertible units from the base unit
    const convertibleUnits = unitConversions
      .filter(conv => conv.from_unidad_medida_id === baseUnitId)
      .map(conv => conv.to_unidad_medida_id);
    
    // Find convertible units to the base unit (reverse conversions)
    const reverseConvertibleUnits = unitConversions
      .filter(conv => conv.to_unidad_medida_id === baseUnitId)
      .map(conv => conv.from_unidad_medida_id);
    
    // Combine all convertible unit IDs
    const allConvertibleUnitIds = [...convertibleUnits, ...reverseConvertibleUnits];
    
    // Add convertible units to available units
    const additionalUnits = unidadMedidaOptions.filter(unit => 
      allConvertibleUnitIds.includes(unit.id) && !availableUnits.some(au => au.id === unit.id)
    );
    
    return [...availableUnits, ...additionalUnits];
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([getAllUnidadesMedida(), getAllIngredientsByLocalId(localId), getAllUnitConversions()])
      .then(([umResp, ingResp, convResp]: any[]) => {
        if (!mounted) return;
        if (umResp) {
          const umData = umResp.data.map((um: any) => ({
            id: um.unidad_medida_id,
            name: `${um.name} - ${um.acronym}`,
          }));
          setUnidadMedidaOptions(umData);
        }
        if (ingResp) {
          const ingData = ingResp.data
            .filter((ing: any) => ing.ingredient_id !== currentIngredientId) // Exclude current ingredient
            .map((ing: any) => ({
              id: ing.ingredient_id,
              name: ing.ingredient_name,
              unidad_medida_id: ing.unidad_medida_id, // Include base unit info
            }));
          // Sort by name
          ingData.sort((a: any, b: any) => a.name.localeCompare(b.name));
          setIngredientOptions(ingData);
        }
        if (convResp) {
          setUnitConversions(convResp.data || []);
        }
      })
      .catch((err) => console.error('Error loading ingredient data', err))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [localId, currentIngredientId]);

  const handleIngredientChange = (index: number, field: keyof IngredientSelection, value: any) => {
    const updated = [...ingredientSelections];
    updated[index] = { ...updated[index], [field]: value } as IngredientSelection;
    
    if (field === 'id') {
      const sel = ingredientOptions.find((opt) => opt.id === value);
      if (sel) {
        updated[index].name = sel.name;
        // Reset unit to the ingredient's default unit
        if (sel.unidad_medida_id) {
          updated[index].unidad_medida_id = sel.unidad_medida_id;
        }
      }
    }
    setIngredientSelections(updated);
  };

  const handleAddRow = () => {
    const defaultUm = unidadMedidaOptions[0]?.id || 1;
    setIngredientSelections([
      ...ingredientSelections,
      { id: '', name: '', units: 1, unidad_medida_id: defaultUm },
    ]);
  };

  const handleRemove = (idx: number) => setIngredientSelections(ingredientSelections.filter((_, i) => i !== idx));

  return (
    <div className="ingredient-composition-manager">
      <div className="config-section">
        <div className="section-header">
          <div className="section-title-group">
            <h6 className="section-title">Composición del Ingrediente</h6>
            {ingredientSelections.length > 0 && (
              <span className="ingredient-count">({ingredientSelections.length} ingredientes)</span>
            )}
          </div>
          <Button
            variant="none"
            className="add-ingredient-btn"
            onClick={handleAddRow}
            disabled={loading}
          >
            <FaPlus /> Agregar Ingrediente
          </Button>
        </div>

        {loading && (
          <div className="loading-row">Cargando ingredientes disponibles...</div>
        )}

        {!loading && ingredientSelections.length === 0 && (
          <div className="empty-state" onClick={handleAddRow}>
            <FaPlus />
            <span>Haz clic para agregar el primer ingrediente</span>
          </div>
        )}

        {!loading && ingredientSelections.length > 0 && (
          <div className="ingredient-rows">
            {ingredientSelections.map((selection, index) => {
              const availableUnits = getAvailableUnitsForIngredient(selection.id);
              const hasUnitRestriction = availableUnits.length < unidadMedidaOptions.length;

              return (
                <div key={index} className="ingredient-row">
                  <div className="qty">
                    <Form.Control
                      type="number"
                      value={selection.units || ''}
                      onChange={(e) => handleIngredientChange(index, 'units', parseFloat(e.target.value) || 0)}
                      placeholder="Cantidad"
                      className="modern-input quantity-input"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="um col">
                    {selection.id && hasUnitRestriction && (
                      <div className="unit-restriction-badge">
                        <FaInfoCircle /> Unidades limitadas
                      </div>
                    )}
                    <SearchDropdown
                      items={availableUnits}
                      selected={availableUnits.find(u => u.id === selection.unidad_medida_id) || null}
                      setSelected={(unit: Option) => handleIngredientChange(index, 'unidad_medida_id', unit.id)}
                      displayProperty="name"
                      idProperty="id"
                      placeholder={availableUnits.length === 0 ? 'Sin unidades' : 'Unidad'}
                      disabled={availableUnits.length === 0 || !selection.id}
                    />
                  </div>

                  <div className="ing">
                    <SearchDropdown
                      items={ingredientOptions}
                      selected={ingredientOptions.find(i => i.id === selection.id) || null}
                      setSelected={(ingredient: Option) => handleIngredientChange(index, 'id', ingredient.id)}
                      displayProperty="name"
                      idProperty="id"
                      placeholder={ingredientOptions.length === 0 ? 'Cargando...' : 'Buscar ingrediente'}
                      disabled={ingredientOptions.length === 0}
                    />
                  </div>

                  <div className="actions">
                    <Button
                      variant="none"
                      className="action-button delete"
                      onClick={() => handleRemove(index)}
                      title="Eliminar ingrediente"
                    >
                      <FaTimes />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default IngredientCompositionManager;
